import { createFileRoute } from '@tanstack/react-router';
import React, { useRef } from 'react';
import generatePDF, { Resolution, Margin } from 'react-to-pdf';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { tableHeadClass, tableCellClass } from '@/components/ui/table-styles';

const options = {
  method: 'open' as const,
  resolution: Resolution.MEDIUM, // MAKE SURE THIS IS NEVER CHANGE. It'll crash in HIGH RESOLUTION.
  page: {
    margin: Margin.SMALL,
    format: 'A4',
    orientation: 'portrait' as const,
  },
  canvas: {
    mimeType: 'image/png' as const,
    qualityRatio: 1,
  },
  overrides: {
    pdf: {
      compress: true,
    },
    canvas: {
      useCORS: true,
    },
  },
};

const QR_URL = 'https://hexdocs.pm/qr_code/docs/qrcode.svg';
const LOGO_URL = '/icon.png';

const Pdf2Sample = () => {
  const targetRef = useRef<HTMLDivElement>(null);

  const rows = Array.from({ length: 30 }, (_, i) => i + 1);

  const handleGeneratePDF = () => {
    if (targetRef.current) {
      generatePDF(targetRef, {
        ...options,
        filename: 'sample.pdf',
      });
    }
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-white p-4">
      <button
        onClick={handleGeneratePDF}
        className="mb-6 px-6 py-2 bg-blue-600 text-white rounded shadow hover:bg-blue-700 transition"
      >
        Open PDF in New Tab
      </button>
      <div
        ref={targetRef}
        className="w-[794px] bg-white p-4"
        style={{ boxShadow: 'none' }}
      >
        {/* Header */}
        <div className="flex justify-between items-start pb-2 mb-6">
          <div className="flex items-start gap-3">
            <img
              src={LOGO_URL}
              alt="Neulink Logo"
              className="w-14 h-14 object-contain border border-gray-300 rounded-md p-2 bg-white"
            />
            <div>
              <div className="font-bold text-lg text-black">Neulink FZ-LLC</div>
              <div className="text-xs text-black">License No: 103492 | Dubai Internet City</div>
              <div className="text-xs text-black">hello@neulink.cloud | +971 56 696 3159</div>
            </div>
          </div>
          <div className="flex flex-col items-end gap-2">
            <div className="text-xs text-black font-medium">30th November 2023</div>
            <div className="w-24 h-4 bg-white" />
          </div>
        </div>
        {/* Bill/Ship To & QR */}
        <div className="flex justify-between items-start mb-8">
          <div className="flex-1 pr-2">
            <div className="font-semibold text-sm mb-1 text-black">Bill To</div>
            <div className="text-xs text-black">John Doe</div>
            <div className="text-xs text-black">123 Main Street</div>
            <div className="text-xs text-black">Dubai, UAE</div>
            <div className="text-xs text-black">+971 50 123 4567</div>
          </div>
          <div className="flex-1 pr-2">
            <div className="font-semibold text-sm mb-1 text-black">Ship To</div>
            <div className="text-xs text-black">Jane Smith</div>
            <div className="text-xs text-black">456 Market Road</div>
            <div className="text-xs text-black">Abu Dhabi, UAE</div>
            <div className="text-xs text-black">+971 55 987 6543</div>
          </div>
          <div className="flex flex-col items-center ml-4">
            <img src={QR_URL} alt="QR Code" className="w-20 h-20 mb-1 object-contain" />
            <span className="text-[10px] text-blue-700">Electronically Generated</span>
          </div>
        </div>
        {/* Table Section */}
        <div className="mb-8">
          <div className="overflow-x-auto">
            <Table className="w-full text-xs border border-gray-200">
              <TableHeader>
                <TableRow>
                  <TableHead className={tableHeadClass + ' text-center'}>SL No.</TableHead>
                  <TableHead className={tableHeadClass + ' text-center'} colSpan={2}>Item Description</TableHead>
                  <TableHead className={tableHeadClass + ' text-center'}>Quantity</TableHead>
                  <TableHead className={tableHeadClass + ' text-center'}>₹ Rate</TableHead>
                  <TableHead className={tableHeadClass + ' text-center'}>₹ Disc.</TableHead>
                  <TableHead className={tableHeadClass + ' text-center'}>₹ Taxable Value</TableHead>
                  <TableHead className={tableHeadClass + ' text-center'}>₹ GST</TableHead>
                  <TableHead className={tableHeadClass + ' text-center'}>₹ Cess</TableHead>
                  <TableHead className={tableHeadClass + ' text-center'}>₹ Amount</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((i) => (
                  <TableRow key={i}>
                    <TableCell className={tableCellClass + ' text-center'}>{i}</TableCell>
                    <TableCell className={tableCellClass + ' text-center'} colSpan={2}>Sample Item {i}</TableCell>
                    <TableCell className={tableCellClass + ' text-center'}>2</TableCell>
                    <TableCell className={tableCellClass + ' text-center'}>100.00</TableCell>
                    <TableCell className={tableCellClass + ' text-center'}>0.00</TableCell>
                    <TableCell className={tableCellClass + ' text-center'}>200.00</TableCell>
                    <TableCell className={tableCellClass + ' text-center'}>5%</TableCell>
                    <TableCell className={tableCellClass + ' text-center'}>0.00</TableCell>
                    <TableCell className={tableCellClass + ' text-center'}>210.00</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
        {/* Payment Details, Notes, T&C, Total */}
        <div className="flex gap-4 pt-8 border-t border-black bg-white">
          <div className="flex-1">
            <div className="font-semibold text-sm mb-1 text-black">Payment Details</div>
            <div className="flex items-center gap-2 mb-2">
              <div>
                <div className="text-xs text-black">Bank: Emirates NBD</div>
                <div className="text-xs text-black">A/C: 1234567890</div>
                <div className="text-xs text-black">IBAN: AE12345678901234567890</div>
              </div>
              <img src={QR_URL} alt="QR Code" className="w-14 h-14 object-contain" />
              <div>
                <div className="text-xs text-black">SWIFT: EBILAEAD</div>
                <div className="text-xs text-black">Branch: Dubai</div>
              </div>
            </div>
            <div className="font-semibold text-sm mb-1 text-black">Notes</div>
            <div className="text-xs text-black mb-2">Thank you for your business.</div>
            <div className="font-semibold text-sm mb-1 text-black">T&C</div>
            <div className="text-xs text-black">Payment due within 30 days. Goods once sold will not be taken back.</div>
          </div>
          <div className="flex flex-col items-end justify-between min-w-[160px]">
            <div>
              <div className="text-xs text-black">Subtotal: 1260.00</div>
              <div className="text-xs text-black mb-4">GST (5%): 63.00</div>
            </div>
            <div className="text-xl font-bold mb-2 text-black">TOTAL: 1323.00</div>
            <div className="mb-2 text-black text-xs">Signature</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const Route = createFileRoute('/pdf2/')({
  component: Pdf2Sample,
});
